/**
 * The click-time doorway every object opens through.
 *
 * Matt, 2026-07-20: clicking a goal, an issue, a customer should show the peek
 * FIRST, with the full page one Expand away. `objectHref()` is already the
 * single source of the object-link SHAPE (every row, card and relationship
 * routes through it), so this is its click-time sibling: same href, different
 * default action.
 *
 * PROGRESSIVE ENHANCEMENT — deliberate, not incidental. Every component here
 * renders a REAL anchor at the canonical `/o/<uid>` href, so cmd/ctrl-click,
 * middle-click, "open in new tab", right-click-copy-link and no-JS all still
 * reach the page. Only a plain left-click is intercepted, and it writes
 * `?peek=<uid>` — a shareable URL the global `ObjectPeekHost` reads, so Back
 * closes the peek and a pasted link opens it. The href is never a lie about
 * where the thing lives.
 *
 * Opening reads `window.location` at click time so two rapid selections cannot
 * make a history decision from stale render state. The anchor separately reads
 * the host's router search seam so its disclosure state follows Back, Forward,
 * and copied URLs without coupling this shared package to a concrete router.
 */

import * as React from "react";
import {
  useNavigate as useRouter,
  RouterLink as NextLink,
  useSearch as useSearchParams,
} from "../router";
import { objectHref, objectUidFromHref } from "../path-label";

/** The query param the global peek host reads. One per app. */
export const PEEK_PARAM = "peek";
export const ACTIVE_PANE_PARAM = "pane";
export const OBJECT_PEEK_OPEN_EVENT = "excellent:object-peek-opening";

export function activeInspectorUid(search: URLSearchParams): string | null {
  const uid = search.get(PEEK_PARAM)?.trim();
  return uid || null;
}

export function isObjectInspectorActive(search: URLSearchParams, uid: string): boolean {
  return activeInspectorUid(search) === uid;
}

export function objectInspectorNavigation(search: URLSearchParams): "push" | "replace" {
  // An open Inspector owns one history entry. Selecting another object changes
  // that entry's subject instead of creating a stack of transient inspections.
  return activeInspectorUid(search) ? "replace" : "push";
}

/** Rewrite the live URL's query string, preserving every other param. */
function withParams(mutate: (p: URLSearchParams) => void): string {
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  mutate(params);
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

/**
 * Returns `open(uid)` — writes `?peek=<uid>` onto the current route. A pane open
 * is a real navigation state: Back closes it and Forward restores it. Replacing
 * the current entry made the inspector impossible to unwind predictably.
 */
export function useOpenObject(): (uid: string) => void {
  const router = useRouter();
  return React.useCallback(
    (uid: string) => {
      window.dispatchEvent(new CustomEvent(OBJECT_PEEK_OPEN_EVENT, { detail: { uid } }));
      const search = new URLSearchParams(window.location.search);
      const navigate = objectInspectorNavigation(search) === "replace"
        ? router.replace
        : router.push;
      navigate(withParams((p) => {
        p.set(PEEK_PARAM, uid);
        p.set(ACTIVE_PANE_PARAM, "inspector");
      }), { scroll: false });
    },
    [router],
  );
}

/**
 * Returns `go(href)` — the imperative sibling of `MaybeObjectLink`, for
 * primitives whose click handler is a callback rather than an anchor (a
 * `DataTable` row, a Board card, a kanban tile). Opens the peek when the href
 * is a canonical object page, navigates otherwise. One implementation so the
 * "prefer the peek" rule can't drift between the link path and the click path.
 */
export function useNavigateOrPeek(): (href: string) => void {
  const router = useRouter();
  const open = useOpenObject();
  return React.useCallback(
    (href: string) => {
      const uid = objectUidFromHref(href);
      if (uid) open(uid);
      else router.push(href);
    },
    [open, router],
  );
}

/** Returns `close()` — drops `?peek` from the current route. */
export function useCloseObject(): () => void {
  const router = useRouter();
  return React.useCallback(() => {
    router.replace(
      withParams((p) => {
        p.delete(PEEK_PARAM);
        p.delete(ACTIVE_PANE_PARAM);
      }),
      { scroll: false },
    );
  }, [router]);
}

/** Shared click guard: let the browser handle every "give me the real page" gesture. */
function wantsNavigation(e: React.MouseEvent): boolean {
  return (
    e.defaultPrevented ||
    e.metaKey ||
    e.ctrlKey ||
    e.shiftKey ||
    e.altKey ||
    e.button !== 0
  );
}

type AnchorProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href">;

/**
 * The raw peek anchor: a real `<a>` at `href` whose plain left-click opens the
 * object peek instead. Assumes `href` IS a canonical object href — callers that
 * can't guarantee that should use the `Link` atom or `MaybeObjectLink`, which
 * check first. Exported so the `Link` atom (a server-safe module) can delegate
 * to it without pulling the whole app across the client boundary.
 */
export function PeekAnchor({
  href,
  uid,
  children,
  onClick,
  ...rest
}: AnchorProps & { href: string; uid: string; children: React.ReactNode }) {
  const open = useOpenObject();
  const search = useSearchParams();
  const expanded = isObjectInspectorActive(search, uid);
  return (
    <a
      {...rest}
      href={href}
      data-object-uid={uid}
      aria-controls="vp-inspector-pane"
      aria-expanded={expanded}
      onClick={(e) => {
        onClick?.(e);
        if (wantsNavigation(e)) return;
        e.preventDefault();
        open(uid);
      }}
    >
      {children}
    </a>
  );
}

export interface ObjectLinkProps extends AnchorProps {
  /** The object's ULID. */
  uid: string;
  /** Origin mode carried on the underlying href (`?from=<mode>`). */
  from?: string | null;
  children: React.ReactNode;
}

/** Link to an object you have the uid for. Always peeks. */
export function ObjectLink({ uid, from, children, ...rest }: ObjectLinkProps) {
  return (
    <PeekAnchor href={objectHref(uid, from)} uid={uid} {...rest}>
      {children}
    </PeekAnchor>
  );
}

/**
 * The href-taking sibling, for generic row/list primitives handed a ready-made
 * link that can't know what it points at (`FeedList`, attention rails). Peeks
 * when the href is a canonical object page, renders an ordinary `next/link`
 * otherwise — so one primitive serves "open the deal" and "go to settings"
 * without its callers branching.
 *
 * Prefer `ObjectLink` when the call site HAS the uid: it says what it means.
 */
export function MaybeObjectLink({
  href,
  children,
  ...rest
}: AnchorProps & { href: string; children: React.ReactNode }) {
  const uid = objectUidFromHref(href);
  if (!uid) {
    return (
      <NextLink href={href} {...rest}>
        {children}
      </NextLink>
    );
  }
  return (
    <PeekAnchor href={href} uid={uid} {...rest}>
      {children}
    </PeekAnchor>
  );
}
